import React, {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useSearchParams } from 'react-router-dom'

import { usePrevious } from '@fourthwall/components'

import { useCurrentShopContext } from '../../../hooks/useCurrentShopContext'
import { useThemeEditorContext } from '../../../hooks/useThemeEditorContext'
import { useActiveThemeContext } from '../../../providers/ActiveThemeProvider'
import { useDirtyContext } from '../../../providers/DirtyProvider'
import { useInstalledThemesQuery } from '../hooks/useInstalledThemesQuery'
import { useThemeLibraryQuery } from '../hooks/useThemeLibraryQuery'
import type { GetInstalledThemesResponse, GetThemeLibraryResponse, InstalledTheme } from '../types'
import { getThemeList } from '../utils'

type InstalledThemesContextType = {
  themeList: InstalledTheme[]
  libraryThemes: GetThemeLibraryResponse['themes']
  currentTheme?: GetInstalledThemesResponse['themes'][number]
  selectedTheme?: InstalledTheme
  isLoadingThemeList: boolean
  isDirty: boolean
  setThemeIsProcessing: (handle: string, isProcessing?: boolean) => void
  refetchThemeList: () => Promise<any>
}

const InstalledThemesContext = createContext<InstalledThemesContextType | undefined>(undefined)

type Props = {
  children: ReactNode
}

export const InstalledThemesProvider = ({ children }: Props) => {
  const { currentShop } = useCurrentShopContext()
  const { resetPreviewTimestamp } = useThemeEditorContext()
  const { isDirty } = useDirtyContext()
  const [refetchInterval, setRefetchInterval] = useState<number | false>(false)
  const { installedThemesQuery } = useInstalledThemesQuery([{ shop_id: currentShop.id }], {
    refetchInterval,
  })
  const { themeLibraryQuery } = useThemeLibraryQuery([], { refetchInterval })
  const [searchParams] = useSearchParams()
  // comes from onboarding flow
  const defaultThemeId = searchParams.get('theme_id')
  const { activeTheme, setActiveTheme } = useActiveThemeContext()

  const installedThemes = installedThemesQuery.data?.themes
  const availableThemes = useMemo(
    () => installedThemes?.filter((theme) => theme.role !== 'main') || [],
    [installedThemes],
  )
  const libraryThemes = themeLibraryQuery.data?.themes || []
  const currentTheme = installedThemes?.find((theme) => theme.role === 'main')
  const isAnyThemeProcessing = installedThemes?.some(
    (theme) => theme.status === 'Duplicating' || theme.status === 'Installing',
  )

  const [themeList, setThemeList] = useState(
    getThemeList(currentTheme, availableThemes, activeTheme.id),
  )
  const selectedTheme = themeList.find((theme) => theme.id === activeTheme.id)
  const isSelectedThemeProcessing = !!selectedTheme?.isProcessing
  const prevIsSelectedThemeProcessing = usePrevious<boolean>(isSelectedThemeProcessing)

  useEffect(() => {
    setThemeList(getThemeList(currentTheme, availableThemes, activeTheme.id))
  }, [installedThemes, activeTheme.id, availableThemes])

  useEffect(() => {
    if (defaultThemeId && availableThemes.some((theme) => theme.id === defaultThemeId)) {
      setActiveTheme({ id: defaultThemeId, name: 'Main' })
    }
  }, [availableThemes, defaultThemeId])

  useEffect(() => {
    if (prevIsSelectedThemeProcessing && !isSelectedThemeProcessing) {
      resetPreviewTimestamp()
    }
  }, [prevIsSelectedThemeProcessing, isSelectedThemeProcessing])

  useEffect(() => {
    if (isAnyThemeProcessing) {
      setRefetchInterval(2000)
    } else {
      setRefetchInterval(false)
    }
  }, [isAnyThemeProcessing])

  const setThemeIsProcessing = (handle: string, isProcessing = true) => {
    setThemeList((themeList) =>
      themeList.map((theme) => {
        if (theme.handle === handle) {
          return { ...theme, isProcessing }
        }

        return theme
      }),
    )
  }

  const value = useMemo(
    () => ({
      themeList,
      libraryThemes,
      currentTheme,
      selectedTheme,
      isLoadingThemeList: installedThemesQuery.isLoading,
      isDirty,
      setThemeIsProcessing,
      refetchThemeList: installedThemesQuery.refetch,
    }),
    [
      themeList,
      libraryThemes,
      currentTheme,
      selectedTheme,
      installedThemesQuery.isLoading,
      isDirty,
      installedThemesQuery.refetch,
    ],
  )

  return <InstalledThemesContext.Provider value={value}>{children}</InstalledThemesContext.Provider>
}

export const useInstalledThemesContext = () => {
  const context = useContext(InstalledThemesContext)
  if (context === undefined) {
    throw new Error('useInstalledThemes must be used within a InstalledThemesProvider')
  }

  return context
}
